import React, { useState, useEffect, useRef, useReducer, useCallback } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useWhatsAppAuth, normalizePhoneNumber } from '../../hooks/useWhatsAppAuth'
import { trackRegistrationComplete, trackSignUpStarted } from '../../lib/tracking'
import { supabase } from '../../services/supabase'
import { motion, AnimatePresence } from 'framer-motion'
import './ChatBot.css'

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface ProductCard {
  id: string
  product_type: 'tyre' | 'rim'
  brand: string
  model: string
  size: string
  bolt_pattern?: string | null
  price: number
  deal_price?: number | null
  image_url?: string | null
  shop_name: string
  shop_address?: string
  shop_city: string
  shop_phone?: string
  shop_link: string
  distance_km?: number | null
}

interface ChatMessage {
  id: string
  text: string
  type: 'user' | 'bot'
  image?: string
  caption?: string
  products?: ProductCard[]
}

type AuthStep =
  | 'idle'
  | 'pick_method'
  | 'pick_action'
  | 'email_login_email'
  | 'email_login_password'
  | 'email_reg_name'
  | 'email_reg_email'
  | 'email_reg_password'
  | 'email_reg_phone'
  | 'email_reg_vehicle_make'
  | 'email_reg_vehicle_model'
  | 'email_reg_vehicle_year'
  | 'email_reg_consent'
  | 'wa_login_phone'
  | 'wa_login_otp'
  | 'wa_reg_phone'
  | 'wa_reg_name'
  | 'wa_reg_vehicle_make'
  | 'wa_reg_vehicle_model'
  | 'wa_reg_vehicle_year'
  | 'wa_reg_consent'
  | 'wa_reg_otp'

type AuthMethod = 'email' | 'whatsapp' | null

interface TempAuthData {
  method?: AuthMethod
  email?: string
  password?: string
  name?: string
  phone?: string
  whatsapp_number?: string
  vehicle_make?: string
  vehicle_model?: string
  vehicle_year?: string
  marketing_consent?: boolean
}

interface ChatState {
  messages: ChatMessage[]
  authStep: AuthStep
  tempAuth: TempAuthData
  isTyping: boolean
  locationStatus: 'idle' | 'requesting' | 'granted' | 'denied'
  userLocation: { lat: number; lng: number } | null
  sessionId: string | null
}

type ChatAction =
  | { type: 'ADD_MESSAGE'; message: ChatMessage }
  | { type: 'SET_TYPING'; value: boolean }
  | { type: 'SET_AUTH_STEP'; step: AuthStep }
  | { type: 'SET_TEMP_AUTH'; data: Partial<TempAuthData> }
  | { type: 'CLEAR_TEMP_AUTH' }
  | { type: 'SET_LOCATION_STATUS'; status: ChatState['locationStatus'] }
  | { type: 'SET_USER_LOCATION'; location: { lat: number; lng: number } }
  | { type: 'SET_SESSION_ID'; id: string }
  | { type: 'LOAD_MESSAGES'; messages: ChatMessage[] }
  | { type: 'RESET' }

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const botMsg = (text: string, image?: string, caption?: string, products?: ProductCard[]): ChatMessage => ({
  id: uid(), text, type: 'bot', image, caption, products
})

const userMsg = (text: string): ChatMessage => ({
  id: uid(), text, type: 'user'
})

const WEBHOOK = '/api/chat/message'

// ─────────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────────

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.message] }
    case 'SET_TYPING':
      return { ...state, isTyping: action.value }
    case 'SET_AUTH_STEP':
      return { ...state, authStep: action.step }
    case 'SET_TEMP_AUTH':
      return { ...state, tempAuth: { ...state.tempAuth, ...action.data } }
    case 'CLEAR_TEMP_AUTH':
      return { ...state, tempAuth: {} }
    case 'SET_LOCATION_STATUS':
      return { ...state, locationStatus: action.status }
    case 'SET_USER_LOCATION':
      return { ...state, userLocation: action.location, locationStatus: 'granted' }
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.id }
    case 'LOAD_MESSAGES':
      return { ...state, messages: action.messages }
    case 'RESET':
      return { ...initialState, sessionId: null }
    default:
      return state
  }
}

const initialState: ChatState = {
  messages: [],
  authStep: 'pick_method',
  tempAuth: {},
  isTyping: false,
  locationStatus: 'idle',
  userLocation: null,
  sessionId: null,
}

// ─────────────────────────────────────────────
// DB helpers
// ─────────────────────────────────────────────

async function dbCreateSession(sessionId: string, userId?: string) {
  await supabase.from('chat_sessions').insert({
    id: sessionId,
    user_id: userId ?? null,
    title: 'New Chat',
    auth_flow: 'none',
  })
}

async function dbSaveMessage(sessionId: string, msg: ChatMessage) {
  await supabase.from('chat_messages').insert({
    session_id: sessionId,
    text: msg.text,
    type: msg.type,
    image: msg.image ?? null,
    caption: msg.caption ?? null,
  })
}

async function dbLinkSession(sessionId: string, userId: string) {
  await supabase
    .from('chat_sessions')
    .update({ user_id: userId, updated_at: new Date().toISOString() })
    .eq('id', sessionId)
}

async function dbLoadSession(userId: string): Promise<{ sessionId: string; messages: ChatMessage[] } | null> {
  const { data: sess, error } = await supabase
    .from('chat_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .single()

  if (error || !sess) return null

  const { data: rows } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('session_id', sess.id)
    .order('created_at', { ascending: true })

  const messages: ChatMessage[] = (rows ?? []).map((m: any) => ({
    id: uid(),
    text: m.text,
    type: m.type as 'user' | 'bot',
    image: m.image ?? undefined,
    caption: m.caption ?? undefined,
  }))

  return { sessionId: sess.id, messages }
}

// ─────────────────────────────────────────────
// Product Card Component
// ─────────────────────────────────────────────

const ProductCards: React.FC<{ products: ProductCard[] }> = ({ products }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
    {products.map((p, i) => (
      <div
        key={i}
        style={{
          display: 'block',
          background: 'white',
          borderRadius: '10px',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
          color: 'inherit',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      >
        {p.image_url && (
          <img
            src={p.image_url}
            alt={`${p.brand} ${p.model}`}
            style={{ width: '100%', height: '130px', objectFit: 'contain', background: '#f9fafb', display: 'block' }}
            onError={e => {
              // Real image failed to load -> reveal the clearly-labelled placeholder.
              e.currentTarget.style.display = 'none'
              const ph = e.currentTarget.nextElementSibling as HTMLElement | null
              if (ph) ph.style.display = 'flex'
            }}
          />
        )}
        {/* Placeholder shown when there is no photo (or the photo fails to load).
            Deliberately made to look NOTHING like a real product photo — diagonal
            stripes + an explicit label — so nobody mistakes it for the item. */}
        <div
          style={{
            width: '100%', height: '130px',
            background: 'repeating-linear-gradient(45deg,#eef1f4,#eef1f4 11px,#e2e7ec 11px,#e2e7ec 22px)',
            display: p.image_url ? 'none' : 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px'
          }}
        >
          <div style={{ fontSize: '24px', opacity: 0.45 }}>🛞</div>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#6b7280', letterSpacing: '0.6px' }}>
            NO PHOTO · PLACEHOLDER
          </div>
          <div style={{ fontSize: '9px', color: '#9ca3af' }}>Actual product may differ</div>
        </div>
        <div style={{ padding: '8px 10px' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', color: '#111827' }}>{p.brand} {p.model}</div>
          <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>{p.size}</div>
          {p.bolt_pattern && (
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>PCD: {p.bolt_pattern}</div>
          )}
          {p.product_type === 'rim' ? (
            <div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                R{(p.deal_price || p.price).toLocaleString()} each
                {p.deal_price && (
                  <span style={{ textDecoration: 'line-through', marginLeft: '6px' }}>
                    R{p.price.toLocaleString()}
                  </span>
                )}
              </div>
              <div style={{ fontWeight: 700, color: '#1E40AF', fontSize: '13px' }}>
                Set of 4: R{((p.deal_price || p.price) * 4).toLocaleString()}
              </div>
            </div>
          ) : (
            <div style={{ fontWeight: 700, color: '#1E40AF', fontSize: '13px' }}>
              R{p.price.toLocaleString()}
              {p.deal_price && (
                <span style={{ fontSize: '11px', color: '#dc2626', marginLeft: '6px' }}>
                  Deal: R{p.deal_price.toLocaleString()}
                </span>
              )}
            </div>
          )}
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '6px', paddingTop: '6px' }}>
            {p.shop_is_online ? (
              <>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151' }}>
                  🛒 {p.shop_name}
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#1E40AF', background: '#e3f2fd', borderRadius: '4px', padding: '1px 5px', marginLeft: '6px' }}>ONLINE STORE</span>
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>🚚 Ships nationwide</div>
                {p.shop_website && (
                  <a
                    href={p.shop_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '12px', color: '#1E40AF', fontWeight: 600, textDecoration: 'none', display: 'inline-block', marginTop: '4px' }}
                  >
                    🔗 Order online
                  </a>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#374151' }}>🏪 {p.shop_name}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  📍 {p.shop_address ? `${p.shop_address}, ` : ''}{p.shop_city}
                  {p.distance_km ? ` · ${p.distance_km.toFixed(1)}km` : ''}
                </div>
                {p.shop_phone && (
                  <a
                    href={`tel:${p.shop_phone}`}
                    style={{ fontSize: '12px', color: '#1E40AF', fontWeight: 600, textDecoration: 'none', display: 'inline-block', marginTop: '4px' }}
                  >
                    📞 Call {p.shop_phone}
                  </a>
                )}
              </>
            )}
          </div>
          <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
            Contact the shop directly to confirm stock &amp; price
          </div>
        </div>
      </div>
    ))}
  </div>
)

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

interface ChatBotProps {
  mode?: 'floating' | 'embedded'
}

export const ChatBot: React.FC<ChatBotProps> = ({ mode = 'floating' }) => {
  const embedded = mode === 'embedded'
  const { user, isAuthenticated, login, register } = useAuthStore()
  const whatsappAuth = useWhatsAppAuth()

  const [isOpen, setIsOpen] = useState(embedded)
  const [inputValue, setInputValue] = useState('')
  const [state, dispatch] = useReducer(chatReducer, initialState)
  const [needsLocation, setNeedsLocation] = useState(false)

  const chatBoxRef = useRef<HTMLDivElement>(null)
  const sessionIdRef = useRef<string | null>(null)
  const initializedRef = useRef(false)

  // ── Message helpers ──

  const addBot = useCallback((text: string, image?: string, caption?: string, products?: ProductCard[]) => {
    const msg = botMsg(text, image, caption, products)
    dispatch({ type: 'ADD_MESSAGE', message: msg })
    if (sessionIdRef.current) dbSaveMessage(sessionIdRef.current, msg)
  }, [])

  const addUser = useCallback((text: string) => {
    const msg = userMsg(text)
    dispatch({ type: 'ADD_MESSAGE', message: msg })
    if (sessionIdRef.current) dbSaveMessage(sessionIdRef.current, msg)
  }, [])

  const setStep = useCallback((step: AuthStep) => {
    dispatch({ type: 'SET_AUTH_STEP', step })
  }, [])

  const setTyping = useCallback((v: boolean) => {
    dispatch({ type: 'SET_TYPING', value: v })
  }, [])

  // ── Session init ──

  const startNewSession = useCallback(async (userId?: string) => {
    const id = crypto.randomUUID()
    sessionIdRef.current = id
    dispatch({ type: 'SET_SESSION_ID', id })
    await dbCreateSession(id, userId)
    return id
  }, [])

  useEffect(() => {
    if (initializedRef.current) return
    initializedRef.current = true

    const init = async () => {
      if (isAuthenticated && user) {
        const existing = await dbLoadSession(user.id)
        if (existing && existing.messages.length > 0) {
          sessionIdRef.current = existing.sessionId
          dispatch({ type: 'SET_SESSION_ID', id: existing.sessionId })
          dispatch({ type: 'LOAD_MESSAGES', messages: existing.messages })
          setStep('idle')
          return
        }
        const anonId = localStorage.getItem('tyrelink_anon_session')
        if (anonId) {
          await dbLinkSession(anonId, user.id)
          localStorage.removeItem('tyrelink_anon_session')
        }
        await startNewSession(user.id)
        setStep('idle')
        addBot('Welcome back! How can I help you with your tyres today?')
        return
      }

      const anonId = localStorage.getItem('tyrelink_anon_session')
      if (anonId) {
        sessionIdRef.current = anonId
        dispatch({ type: 'SET_SESSION_ID', id: anonId })
      } else {
        const newId = await startNewSession()
        localStorage.setItem('tyrelink_anon_session', newId)
      }
      setStep('pick_method')
      addBot("👋 Welcome to TyreLink!\n\nTo get started, choose how you'd like to sign in:")
    }

    init()
  }, [])

  // ── Scroll to bottom ──

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight
    }
  }, [state.messages, state.isTyping])

  // ── Keep chat in sync with global auth state ──
  // Global auth settles asynchronously (session fetch + profile queries), so the
  // chat may initialise as anonymous before the store reports the user as signed
  // in. If that happens — or the user signs in through any other path — snap the
  // chat out of its sign-in flow. Only intervenes at the START of the flow
  // (pick_method / pick_action), never mid credential entry, so the chat's own
  // login/register handlers keep full control of their steps.
  useEffect(() => {
    if (
      isAuthenticated && user &&
      (state.authStep === 'pick_method' || state.authStep === 'pick_action')
    ) {
      dispatch({ type: 'CLEAR_TEMP_AUTH' })
      setStep('idle')
      addBot(
        `✅ You're already signed in${user.email ? ` as ${user.email}` : ''}. ` +
        `How can I help you with your tyres today?`
      )
      // Adopt any anonymous chat session into this user's account
      const anonId = localStorage.getItem('tyrelink_anon_session')
      if (anonId) {
        dbLinkSession(anonId, user.id)
          .then(() => localStorage.removeItem('tyrelink_anon_session'))
          .catch(() => { /* non-fatal */ })
      }
    }
  }, [isAuthenticated, user, state.authStep, addBot, setStep])

  // ── New Chat ──

  const handleNewChat = async () => {
    dispatch({ type: 'RESET' })
    setInputValue('')
    setNeedsLocation(false)
    whatsappAuth.resetState()
    localStorage.removeItem('tyrelink_anon_session')

    if (isAuthenticated && user) {
      await startNewSession(user.id)
      setStep('idle')
      addBot('Starting fresh! How can I help you with your tyres?')
    } else {
      const newId = await startNewSession()
      localStorage.setItem('tyrelink_anon_session', newId)
      setStep('pick_method')
      addBot("👋 Welcome to TyreLink!\n\nTo get started, choose how you'd like to sign in:")
    }
  }

  // ─────────────────────────────────────────────
  // Auth Flows
  // ─────────────────────────────────────────────

  const handleEmailLogin = async (input: string) => {
    const { authStep, tempAuth } = state

    if (authStep === 'email_login_email') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { email: input } })
      addUser(input)
      addBot('Please enter your password:')
      setStep('email_login_password')
      return
    }

    if (authStep === 'email_login_password') {
      addUser('••••••••')
      setTyping(true)
      useAuthStore.getState().clearError?.()
      await login(tempAuth.email!, input)
      const { user: freshUser, error: loginError } = useAuthStore.getState()
      setTyping(false)

      if (loginError || !freshUser) {
        addBot(`❌ ${loginError || 'Invalid email or password. Please try again.'}`)
        addBot('Please enter your email address:')
        dispatch({ type: 'SET_TEMP_AUTH', data: { email: undefined, password: undefined } })
        setStep('email_login_email')
        return
      }

      addBot('✅ Welcome back! How can I help you with your tyres today?')
      setStep('idle')
      dispatch({ type: 'CLEAR_TEMP_AUTH' })
      if (sessionIdRef.current) {
        await dbLinkSession(sessionIdRef.current, freshUser.id)
        localStorage.removeItem('tyrelink_anon_session')
      }
    }
  }

  const handleEmailRegister = async (input: string) => {
    const { authStep, tempAuth } = state

    if (authStep === 'email_reg_name') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { name: input } })
      addUser(input)
      addBot('Now enter your email address:')
      trackSignUpStarted('email')
      setStep('email_reg_email')
      return
    }
    if (authStep === 'email_reg_email') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { email: input } })
      addUser(input)
      addBot('Create a password (min. 6 characters):')
      setStep('email_reg_password')
      return
    }
    if (authStep === 'email_reg_password') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { password: input } })
      addUser('••••••••')
      addBot('Enter your phone number:')
      setStep('email_reg_phone')
      return
    }
    if (authStep === 'email_reg_phone') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { phone: input } })
      addUser(input)
      addBot('🚗 Vehicle make? (e.g. Toyota, BMW, Ford)')
      setStep('email_reg_vehicle_make')
      return
    }
    if (authStep === 'email_reg_vehicle_make') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { vehicle_make: input } })
      addUser(input)
      addBot(`Model of your ${input}? (e.g. Corolla, EcoSport)`)
      setStep('email_reg_vehicle_model')
      return
    }
    if (authStep === 'email_reg_vehicle_model') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { vehicle_model: input } })
      addUser(input)
      addBot('Vehicle year? (e.g. 2021)')
      setStep('email_reg_vehicle_year')
      return
    }
    if (authStep === 'email_reg_vehicle_year') {
      addUser(input)
      const year = input.trim()
      dispatch({ type: 'SET_TEMP_AUTH', data: { vehicle_year: year } })
      addBot(
        'Last question! 🎁 Would you like to receive exclusive tyre deals and offers ' +
        'from TyreLink on WhatsApp? (You can opt out anytime.)'
      )
      setStep('email_reg_consent')
      return
    }
    if (authStep === 'email_reg_consent') {
      // Typed answer fallback (buttons are the primary path)
      const answer = input.trim().toLowerCase()
      if (['yes', 'y', 'yes please', 'sure', 'ok'].includes(answer)) {
        addUser(input)
        await finalizeEmailRegistration(true)
      } else if (['no', 'n', 'no thanks', 'nope'].includes(answer)) {
        addUser(input)
        await finalizeEmailRegistration(false)
      } else {
        addUser(input)
        addBot('Please tap Yes or No above, or type "yes" or "no".')
      }
    }
  }

  const finalizeEmailRegistration = async (consent: boolean) => {
    const { tempAuth } = state
    dispatch({ type: 'SET_TEMP_AUTH', data: { marketing_consent: consent } })
    setTyping(true)
    const finalData = { ...tempAuth, marketing_consent: consent }
    const year = finalData.vehicle_year || ''

      try {
        const result = await register(finalData.email!, finalData.password!, {
          full_name: finalData.name,
          phone: finalData.phone,
          role: 'customer',
          vehicle_make: finalData.vehicle_make,
          vehicle_model: finalData.vehicle_model,
          vehicle_year: parseInt(year) || undefined,
          marketing_consent: consent,
        })
        setTyping(false)

        if (result === 'confirm_email') {
          addBot(
            `📧 Almost done, ${finalData.name}!\n\n` +
            `We've sent a confirmation link to ${finalData.email}. ` +
            `Please click it to activate your account, then come back here and choose Login.\n\n` +
            `(Check your spam folder if you don't see it within a minute.)`
          )
          setStep('pick_method')
          dispatch({ type: 'CLEAR_TEMP_AUTH' })
          addBot("Once you've confirmed, choose how you'd like to sign in:")
          return
        }

        const freshUser = useAuthStore.getState().user
        trackRegistrationComplete('email')
        addBot(
          `🎉 Account created! Welcome, ${finalData.name}!\n\n` +
          `Your ${finalData.vehicle_make} ${finalData.vehicle_model} (${year}) is saved.\n\n` +
          `How can I help you today?`
        )
        setStep('idle')
        dispatch({ type: 'CLEAR_TEMP_AUTH' })
        if (freshUser && sessionIdRef.current) {
          await dbLinkSession(sessionIdRef.current, freshUser.id)
          localStorage.removeItem('tyrelink_anon_session')
        }
      } catch (err: any) {
        setTyping(false)
        addBot(`❌ Registration failed: ${err?.message || 'Please try again.'}`)
        setStep('pick_method')
        dispatch({ type: 'CLEAR_TEMP_AUTH' })
        addBot("Let's try again — choose how you'd like to sign in:")
      }
  }

  const finalizeWaRegistration = async (consent: boolean) => {
    const { tempAuth } = state
    dispatch({ type: 'SET_TEMP_AUTH', data: { marketing_consent: consent } })
    setTyping(true)
    const finalData = { ...tempAuth, marketing_consent: consent }
    const year = finalData.vehicle_year || ''

    const ok = await whatsappAuth.sendRegistrationOTP({
      whatsappNumber: finalData.whatsapp_number!,
      fullName: finalData.name || '',
      vehicleMake: finalData.vehicle_make,
      vehicleModel: finalData.vehicle_model,
      vehicleYear: parseInt(year) || undefined,
      role: 'customer',
      marketingConsent: consent,
    })
    setTyping(false)

    if (ok) {
      addBot('✅ OTP sent to your WhatsApp. Enter it below:')
      setStep('wa_reg_otp')
    } else {
      addBot(`❌ ${whatsappAuth.error || 'Failed to send OTP. Please try again.'}`)
      setStep('pick_method')
      dispatch({ type: 'CLEAR_TEMP_AUTH' })
    }
  }

  const handleWaLogin = async (input: string) => {
    const { authStep, tempAuth } = state

    if (authStep === 'wa_login_phone') {
      addUser(input.trim())
      let phone: string
      try {
        phone = normalizePhoneNumber(input)
      } catch (e: any) {
        addBot(`❌ ${e.message}`)
        return
      }
      setTyping(true)
      const exists = await whatsappAuth.checkUserExists(phone)
      if (!exists) {
        setTyping(false)
        dispatch({ type: 'SET_TEMP_AUTH', data: { whatsapp_number: phone } })
        addBot('❌ This number is not registered. Would you like to register instead?')
        setStep('pick_method')
        return
      }
      const ok = await whatsappAuth.sendLoginOTP(phone)
      setTyping(false)
      if (ok) {
        dispatch({ type: 'SET_TEMP_AUTH', data: { whatsapp_number: phone } })
        addBot('✅ A 6-digit OTP was sent to your WhatsApp. Enter it below:')
        setStep('wa_login_otp')
      } else {
        addBot(`❌ ${whatsappAuth.error || 'Failed to send OTP. Please try again.'}`)
        setStep('pick_method')
      }
      return
    }

    if (authStep === 'wa_login_otp') {
      if (input.toLowerCase() === 'resend') {
        addUser('Resend OTP')
        setTyping(true)
        const ok = await whatsappAuth.sendLoginOTP(tempAuth.whatsapp_number!)
        setTyping(false)
        addBot(ok ? '✅ New OTP sent.' : `❌ ${whatsappAuth.error || 'Failed to resend.'}`)
        return
      }
      addUser('••••••')
      setTyping(true)
      const result = await whatsappAuth.verifyLoginOTP(tempAuth.whatsapp_number!, input.trim())
      setTyping(false)
      if (result.success) {
        addBot('🎉 Logged in! Welcome back to TyreLink. How can I help you today?')
        setStep('idle')
        dispatch({ type: 'CLEAR_TEMP_AUTH' })
        const freshUser = useAuthStore.getState().user
        if (freshUser && sessionIdRef.current) {
          await dbLinkSession(sessionIdRef.current, freshUser.id)
          localStorage.removeItem('tyrelink_anon_session')
        }
      } else {
        addBot(`❌ ${result.error || 'Invalid OTP. Try again or type "resend".'}`)
      }
    }
  }

  const handleWaRegister = async (input: string) => {
    const { authStep, tempAuth } = state

    if (authStep === 'wa_reg_phone') {
      addUser(input.trim())
      let phone: string
      try {
        phone = normalizePhoneNumber(input)
      } catch (e: any) {
        addBot(`❌ ${e.message}`)
        return
      }
      dispatch({ type: 'SET_TEMP_AUTH', data: { whatsapp_number: phone } })
      addBot("What's your full name?")
      setStep('wa_reg_name')
      return
    }
    if (authStep === 'wa_reg_name') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { name: input.trim() } })
      addUser(input)
      addBot('🚗 Vehicle make? (e.g. Toyota, Ford, BMW)')
      setStep('wa_reg_vehicle_make')
      return
    }
    if (authStep === 'wa_reg_vehicle_make') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { vehicle_make: input.trim() } })
      addUser(input)
      addBot(`Model of your ${input.trim()}? (e.g. Corolla, Mustang)`)
      setStep('wa_reg_vehicle_model')
      return
    }
    if (authStep === 'wa_reg_vehicle_model') {
      dispatch({ type: 'SET_TEMP_AUTH', data: { vehicle_model: input.trim() } })
      addUser(input)
      addBot('Vehicle year? (e.g. 2021)')
      setStep('wa_reg_vehicle_year')
      return
    }
    if (authStep === 'wa_reg_vehicle_year') {
      addUser(input)
      const year = input.trim()
      dispatch({ type: 'SET_TEMP_AUTH', data: { vehicle_year: year } })
      addBot(
        'Last question! 🎁 Would you like to receive exclusive tyre deals and offers ' +
        'from TyreLink on WhatsApp? (You can opt out anytime.)'
      )
      setStep('wa_reg_consent')
      return
    }

    if (authStep === 'wa_reg_consent') {
      // Typed answer fallback (buttons are the primary path)
      const answer = input.trim().toLowerCase()
      if (['yes', 'y', 'yes please', 'sure', 'ok'].includes(answer)) {
        addUser(input)
        await finalizeWaRegistration(true)
      } else if (['no', 'n', 'no thanks', 'nope'].includes(answer)) {
        addUser(input)
        await finalizeWaRegistration(false)
      } else {
        addUser(input)
        addBot('Please tap Yes or No above, or type "yes" or "no".')
      }
      return
    }

    if (authStep === 'wa_reg_otp') {
      if (input.toLowerCase() === 'resend') {
        addUser('Resend OTP')
        setTyping(true)
        const ok = await whatsappAuth.sendOTP(tempAuth.whatsapp_number!, 'register')
        setTyping(false)
        addBot(ok ? '✅ New OTP sent.' : `❌ ${whatsappAuth.error || 'Failed to resend.'}`)
        return
      }
      addUser('••••••')
      setTyping(true)
      const result = await whatsappAuth.verifyRegistrationOTP(
        tempAuth.whatsapp_number!,
        input.trim(),
        {
          whatsappNumber: tempAuth.whatsapp_number!,
          fullName: tempAuth.name || '',
          vehicleMake: tempAuth.vehicle_make,
          vehicleModel: tempAuth.vehicle_model,
          vehicleYear: parseInt(tempAuth.vehicle_year || '0') || undefined,
          role: 'customer',
        }
      )
      setTyping(false)
      if (result.success) {
        trackRegistrationComplete('whatsapp')
        addBot(
          `🎉 Welcome to TyreLink, ${tempAuth.name}!\n\n` +
          `Your ${tempAuth.vehicle_make} ${tempAuth.vehicle_model} (${tempAuth.vehicle_year}) is saved.\n\n` +
          `You're all set. ✅\n\n` +
          `How can I help today?`
        )
        setStep('idle')
        dispatch({ type: 'CLEAR_TEMP_AUTH' })
        const freshUser = useAuthStore.getState().user
        if (freshUser && sessionIdRef.current) {
          await dbLinkSession(sessionIdRef.current, freshUser.id)
          localStorage.removeItem('tyrelink_anon_session')
        }
      } else {
        addBot(`❌ ${result.error || 'Invalid OTP. Try again or type "resend".'}`)
      }
    }
  }

  // ── Main AI Chat ──

  const handleAIChat = useCallback(async (input: string) => {
    addUser(input)
    setTyping(true)

    try {
      const res = await fetch(WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: sessionIdRef.current,
          name: user?.user_metadata?.full_name || 'User',
          phoneNumber: 'web-user',
          userMessage: input,
          historyText: input,
        }),
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()

      if (data.location_needed === true) {
        const currentLocation = state.userLocation
        if (currentLocation) {
          setTyping(false)
          await handleAIChat(`lat: ${currentLocation.lat} lon: ${currentLocation.lng}`)
          return
        }
        setNeedsLocation(true)
        addBot('📍 I need your location to find nearby tyre shops. Would you like to share it?')
        setTyping(false)
        return
      }

      const products = data.products && data.products.length > 0 ? data.products as ProductCard[] : undefined
      const reply = data.reply || data.message || 'No response.'
      const image = !products && data.image_present === 'true' ? data.image_link : undefined
      const caption = image ? data.caption : undefined
      addBot(reply, image, caption, products)
    } catch {
      addBot('⚠️ Error reaching server. Please try again.')
    } finally {
      setTyping(false)
    }
  }, [user, state.userLocation, addBot, addUser, setTyping])

  // ── Location ──

  const handleShareLocation = () => {
    if (!navigator.geolocation) {
      addBot('❌ Geolocation not supported by your browser.')
      return
    }
    dispatch({ type: 'SET_LOCATION_STATUS', status: 'requesting' })

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        dispatch({ type: 'SET_USER_LOCATION', location: loc })
        setNeedsLocation(false)
        await handleAIChat(`lat: ${loc.lat} lon: ${loc.lng}`)
      },
      (err) => {
        dispatch({ type: 'SET_LOCATION_STATUS', status: 'denied' })
        setNeedsLocation(false)
        const msgs: Record<number, string> = {
          1: 'Location access denied. Enable it in browser settings.',
          2: 'Location unavailable.',
          3: 'Location request timed out.',
        }
        addBot(`❌ ${msgs[err.code] || 'Unable to get location.'}`)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    )
  }

  // ─────────────────────────────────────────────
  // Master send handler
  // ─────────────────────────────────────────────

  const handleSend = async () => {
    const input = inputValue.trim()
    if (!input || state.isTyping) return
    setInputValue('')

    const { authStep } = state

    if (authStep === 'idle') {
      if (isAuthenticated) {
        await handleAIChat(input)
      } else {
        addUser(input)
        addBot('Please sign in to continue chatting.')
        setStep('pick_method')
      }
      return
    }

    if (authStep.startsWith('email_login')) { await handleEmailLogin(input); return }
    if (authStep.startsWith('email_reg'))   { await handleEmailRegister(input); return }
    if (authStep.startsWith('wa_login'))    { await handleWaLogin(input); return }
    if (authStep.startsWith('wa_reg'))      { await handleWaRegister(input); return }

    addUser(input)
    addBot('Please use the buttons below to continue.')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ─────────────────────────────────────────────
  // Input placeholder
  // ─────────────────────────────────────────────

  const getPlaceholder = () => {
    switch (state.authStep) {
      case 'idle': return 'Ask about tyres...'
      case 'email_login_password':
      case 'email_reg_password': return 'Enter password...'
      case 'email_login_email':
      case 'email_reg_email': return 'Enter email...'
      case 'wa_login_otp':
      case 'wa_reg_otp': return 'Enter 6-digit OTP...'
      case 'wa_login_phone':
      case 'wa_reg_phone': return 'e.g. 083 123 4567 or +27 83 123 4567'
      case 'email_reg_name':
      case 'wa_reg_name': return 'Your full name...'
      case 'email_reg_phone': return 'Phone number...'
      case 'email_reg_vehicle_make':
      case 'wa_reg_vehicle_make': return 'e.g. Toyota, BMW, Ford'
      case 'email_reg_vehicle_model':
      case 'wa_reg_vehicle_model': return 'e.g. Corolla, EcoSport'
      case 'email_reg_vehicle_year':
      case 'wa_reg_vehicle_year': return 'e.g. 2021'
      case 'email_reg_consent':
      case 'wa_reg_consent': return 'Yes or No'
      default: return 'Type here...'
    }
  }

  const isPasswordField =
    state.authStep === 'email_login_password' || state.authStep === 'email_reg_password'

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────

  return (
    <>
      {/* Launcher bubble (floating mode only) */}
      {!embedded && (
        <motion.div
          className="chat-launcher"
          onClick={() => setIsOpen(o => !o)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          💬
        </motion.div>
      )}

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={embedded ? 'chat-container chat-container-embedded' : 'chat-container'}
            style={embedded ? {
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            } : {
              position: 'fixed',
              top: 'auto',
              bottom: '90px',
              right: '20px',
              left: 'auto',
              width: '380px',
              maxWidth: 'calc(100vw - 40px)',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="chat-header">
              <span>TyreLink ChatBot</span>
              <div>
                <button onClick={handleNewChat} className="chat-header-btn">New</button>
                {!embedded && (
                  <button onClick={() => setIsOpen(false)} className="chat-header-btn">✕</button>
                )}
              </div>
            </div>

            {/* AI Disclaimer */}
            <div style={{
              backgroundColor: '#fef9c3',
              color: '#713f12',
              fontSize: '11px',
              padding: '6px 12px',
              textAlign: 'center',
              borderBottom: '1px solid #fde68a'
            }}>
              ⚠️ Always confirm with your tyre dealer — AI can make mistakes.
            </div>

            {/* Messages */}
            <div ref={chatBoxRef} className="chat-box">
              {state.messages.map(msg => (
                <div key={msg.id} className={`chat-msg ${msg.type === 'user' ? 'chat-user' : 'chat-bot'}`}>
                  <div className="chat-msg-text">{msg.text}</div>
                  {msg.products && msg.products.length > 0 && (
                    <ProductCards products={msg.products} />
                  )}
                  {!msg.products && msg.image && (
                    <div className="chat-images">
                      <div className="chat-image-wrapper">
                        <img
                          src={msg.image}
                          alt={msg.caption || 'Image'}
                          className="chat-image"
                          onClick={() => window.open(msg.image!, '_blank')}
                          onError={e => { e.currentTarget.style.display = 'none' }}
                        />
                        {msg.caption && (
                          <div className="chat-image-caption">{msg.caption}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* ── Step 1: Pick sign-in method ── */}
              {state.authStep === 'pick_method' && (
                <div className="auth-buttons" style={{ flexDirection: 'column', gap: '8px' }}>
                  <button
                    className="auth-btn login-btn"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: '6px', backgroundColor: '#25D366', borderColor: '#25D366'
                    }}
                    onClick={() => {
                      dispatch({ type: 'SET_TEMP_AUTH', data: { method: 'whatsapp' } })
                      addBot('Would you like to Login or Register with WhatsApp?')
                      setStep('pick_action')
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488"/>
                    </svg>
                    Continue with WhatsApp
                  </button>
                  <button
                    className="auth-btn register-btn"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    onClick={() => {
                      dispatch({ type: 'SET_TEMP_AUTH', data: { method: 'email' } })
                      addBot('Would you like to Login or Register with email?')
                      setStep('pick_action')
                    }}
                  >
                    ✉️ Continue with Email
                  </button>
                </div>
              )}

              {/* ── Step 2: Login or Register ── */}
              {state.authStep === 'pick_action' && (
                <div className="auth-buttons">
                  <button
                    className="auth-btn login-btn"
                    onClick={() => {
                      const isWa = state.tempAuth.method === 'whatsapp'
                      if (isWa) {
                        addBot('Enter your WhatsApp number (e.g. 083 123 4567 or +27 83 123 4567):')
                        setStep('wa_login_phone')
                      } else {
                        addBot('Enter your email address:')
                        setStep('email_login_email')
                      }
                    }}
                  >
                    Login
                  </button>
                  <button
                    className="auth-btn register-btn"
                    onClick={() => {
                      const isWa = state.tempAuth.method === 'whatsapp'
                      if (isWa) {
                        addBot("Let's get you set up! Enter your WhatsApp number:")
                        trackSignUpStarted('whatsapp')
                        setStep('wa_reg_phone')
                      } else {
                        addBot("Let's create your account! What's your full name?")
                        setStep('email_reg_name')
                      }
                    }}
                  >
                    Register
                  </button>
                </div>
              )}

              {/* ── Marketing consent (Yes / No) ── */}
              {(state.authStep === 'email_reg_consent' || state.authStep === 'wa_reg_consent') && (
                <div className="auth-buttons">
                  <button
                    className="auth-btn register-btn"
                    onClick={async () => {
                      addUser('Yes, send me deals 🎉')
                      if (state.authStep === 'email_reg_consent') await finalizeEmailRegistration(true)
                      else await finalizeWaRegistration(true)
                    }}
                  >
                    Yes, send me deals 🎉
                  </button>
                  <button
                    className="auth-btn login-btn"
                    onClick={async () => {
                      addUser('No thanks')
                      if (state.authStep === 'email_reg_consent') await finalizeEmailRegistration(false)
                      else await finalizeWaRegistration(false)
                    }}
                  >
                    No thanks
                  </button>
                </div>
              )}

              {/* ── OTP Resend ── */}
              {(state.authStep === 'wa_login_otp' || state.authStep === 'wa_reg_otp') && (
                <div className="auth-buttons">
                  <button
                    className="auth-btn login-btn"
                    style={{ fontSize: '12px' }}
                    onClick={async () => {
                      setTyping(true)
                      const purpose = state.authStep === 'wa_login_otp' ? 'login' : 'register'
                      const ok = await whatsappAuth.sendOTP(state.tempAuth.whatsapp_number!, purpose as any)
                      setTyping(false)
                      addBot(ok ? '✅ New OTP sent.' : `❌ ${whatsappAuth.error || 'Failed to resend.'}`)
                    }}
                  >
                    🔄 Resend OTP
                  </button>
                </div>
              )}

              {/* ── Location prompt ── */}
              {needsLocation && state.authStep === 'idle' && (
                <div className="auth-buttons">
                  <button
                    className="auth-btn login-btn"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    onClick={handleShareLocation}
                  >
                    📍 Share Location
                  </button>
                  <button
                    className="auth-btn register-btn"
                    onClick={() => {
                      setNeedsLocation(false)
                      addBot('No problem! You can still ask me anything about tyres.')
                    }}
                  >
                    No Thanks
                  </button>
                </div>
              )}

              {/* ── Typing indicator ── */}
              {state.isTyping && (
                <div className="chat-msg chat-bot typing">
                  <div className="typing-indicator">
                    <span /><span /><span />
                  </div>
                </div>
              )}
            </div>

            {/* Input bar */}
            <div className="chat-input-area">
              <input
                type={isPasswordField ? 'password' : 'text'}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={getPlaceholder()}
                className="chat-input"
                disabled={state.isTyping}
              />
              <button
                onClick={handleSend}
                className="send-btn"
                disabled={state.isTyping}
              >
                Send
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default ChatBot
